import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, Ticket, FileText, AlertTriangle, CheckCircle, Printer, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { ClaimStatusBadge, EvidenceDrawer, CitationList, PrimaryDocuments } from "@/components/shared";

const SECTION_CONFIG = {
  concept: { label: "Concept & Origins", icon: "📋" },
  design_iteration: { label: "Design Iterations", icon: "🎨" },
  proof: { label: "Printing Proofs & Negatives", icon: "📄" },
  negative: { label: "Original Negatives", icon: "🎞️" },
  seating_zone: { label: "Seating Zones & Layout", icon: "🪑" },
  print_logic: { label: "Print Logic & Technology", icon: "⚙️" },
  quantities: { label: "Documented Quantities", icon: "📊" },
  distribution: { label: "Distribution Channels", icon: "📦" },
  charity_link: { label: "Charity Programs", icon: "❤️" },
  myths_vs_facts: { label: "Myths vs Facts", icon: "⚖️" },
};

export const TicketsHubPage = () => {
  const [entryCounts, setEntryCounts] = useState({});
  
  useEffect(() => {
    api.get("/tickets").then(res => {
      const counts = {};
      res.data.forEach(entry => {
        counts[entry.section] = (counts[entry.section] || 0) + 1;
      });
      setEntryCounts(counts);
    }).catch(() => {});
  }, []);
  
  const activeSections = Object.entries(SECTION_CONFIG).filter(
    ([key]) => entryCounts[key] > 0
  );
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="tickets-hub-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="mb-16">
          <p className="font-ui text-xs uppercase tracking-[0.25em] text-[hsl(43,74%,52%)] mb-4">Historical Documentation</p>
          <h1 className="font-display text-4xl md:text-5xl text-white mb-6">Ticket History & Design Archive</h1>
          <p className="font-editorial text-lg text-white/60 leading-relaxed max-w-3xl">
            Comprehensive documentation of the Aloha from Hawaii concert tickets, from initial concept 
            through production and distribution. This section represents the core scholarly contribution 
            of the archive, with every claim backed by primary source evidence.
          </p>
        </div>
        
        <div className="grid gap-4 md:grid-cols-2">
          {activeSections.map(([section, config]) => (
            <Link 
              key={section}
              to={`/tickets/${section}`}
              className="p-6 bg-[hsl(222,47%,11%,0.5)] border border-white/10 hover:border-[hsl(43,74%,52%,0.4)] flex items-center justify-between group transition-colors"
              data-testid={`ticket-section-${section}`}
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl">{config.icon}</span>
                <div>
                  <h3 className="font-display text-lg text-white group-hover:text-[hsl(43,74%,52%)] transition-colors">
                    {config.label}
                  </h3>
                  <p className="text-sm font-ui text-white/50">{entryCounts[section]} entries</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/40 group-hover:text-[hsl(43,74%,52%)]" />
            </Link>
          ))}
          
          {activeSections.length === 0 && (
            <div className="col-span-2 text-center py-12 text-white/50 font-editorial">
              No ticket entries have been documented yet.
            </div>
          )}
        </div>
        
        <div className="mt-12 p-6 bg-[hsl(43,40%,15%)] border-l-2 border-[hsl(43,74%,52%)]">
          <h3 className="font-display text-lg text-[hsl(43,74%,52%)] mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
          Evidence Standards
        </h3>
        <p className="font-editorial text-sm text-white/70">
          Every entry in this section is cross-referenced with primary documentation. 
          Claims are classified as Documented, Disputed, or Unverified based on available evidence. 
          Click "Evidence" on any entry to view supporting citations.
        </p>
      </div>
      
      {/* Archive Pack Download */}
      <div className="mt-8 p-6 bg-[hsl(222,47%,11%,0.5)] border border-white/10">
        <h3 className="font-display text-lg text-white mb-3 flex items-center gap-2">
          <Download className="w-5 h-5 text-[hsl(43,74%,52%)]" />
          Archive Pack Download
        </h3>
        <p className="font-editorial text-sm text-white/60 mb-4">
          Download the complete Tickets Archive Pack as a single PDF document. Includes all sections, 
          consolidated footnotes, and integrity verification for institutional archival.
        </p>
        <a
          href={`${API}/pdf/archive/tickets`}
          download
          className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(43,74%,52%)] text-black font-ui text-sm font-semibold uppercase tracking-wider hover:bg-white transition-colors"
          data-testid="download-tickets-archive-btn"
        >
          <FileText className="w-4 h-4" />
          Download Tickets Archive Pack (PDF)
        </a>
      </div>
    </div>
    </div>
  );
};

export const TicketsSectionPage = () => {
  const { section } = useParams();
  const [entries, setEntries] = useState([]);
  const [citations, setCitations] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await api.get(`/tickets?section=${section}`);
        setEntries(res.data);
        
        // Load citations for entries that have citation_ids
        const citationPromises = [];
        res.data.forEach(entry => {
          if (entry.citation_ids?.length > 0) {
            entry.citation_ids.forEach(citId => {
              citationPromises.push(
                api.get(`/citations`).then(citRes => {
                  const found = citRes.data.find(c => c.id === citId);
                  return found ? { entryId: entry.id, citation: found } : null;
                }).catch(() => null)
              );
            });
          }
        });
        
        const citationResults = await Promise.all(citationPromises);
        const citationMap = {};
        citationResults.filter(Boolean).forEach(({ entryId, citation }) => {
          if (!citationMap[entryId]) citationMap[entryId] = [];
          citationMap[entryId].push(citation);
        });
        setCitations(citationMap);
      } catch (err) {
        console.error("Failed to load ticket data", err);
      }
      setLoading(false);
    };
    
    loadData();
  }, [section]);
  
  const sectionConfig = SECTION_CONFIG[section] || { label: section, icon: "📄" };
  
  // Calculate status rollup for each entry
  const getStatusRollup = (entry) => {
    if (!entry.claim_ids || entry.claim_ids.length === 0) return null;
    // Would need to fetch claims to determine this accurately
    // For now, return based on section heuristics
    if (section === "myths_vs_facts") return "Disputed";
    return "Documented";
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-20" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-[hsl(43,74%,52%)]"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }} data-testid="tickets-section-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0 bg-[hsl(222,47%,8%)] border-r border-white/10 min-h-screen sticky top-20">
            <div className="p-4 border-b border-white/10">
              <Link to="/tickets" className="font-ui text-sm text-[hsl(43,74%,52%)] hover:text-white flex items-center gap-1 transition-colors">
                ← Back to Tickets
              </Link>
            </div>
            <ScrollArea className="h-[calc(100vh-140px)]">
              <nav className="p-4">
                <h3 className="font-ui text-xs uppercase tracking-[0.2em] text-white/40 mb-3">
                  Sections
                </h3>
                {Object.entries(SECTION_CONFIG).map(([key, config]) => (
                  <Link
                    key={key}
                    to={`/tickets/${key}`}
                    className={`block px-3 py-2.5 font-ui text-sm mb-1 transition-colors ${
                      section === key 
                        ? "bg-[hsl(43,74%,52%,0.1)] text-[hsl(43,74%,52%)] border-l-2 border-[hsl(43,74%,52%)]" 
                        : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <span className="mr-2">{config.icon}</span>
                    {config.label}
                  </Link>
                ))}
              </nav>
            </ScrollArea>
          </aside>
          
          {/* Main Content */}
          <main className="flex-1 p-8 md:p-12">
            <header className="mb-10">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{sectionConfig.icon}</span>
                  <h1 className="font-display text-3xl md:text-4xl text-white">{sectionConfig.label}</h1>
                </div>
                <div className="flex items-center gap-3">
                  <a 
                    href={`${API}/pdf/tickets/${section}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-ui text-sm hover:border-[hsl(43,74%,52%)] hover:text-[hsl(43,74%,52%)] transition-colors"
                    data-testid="export-pdf-btn"
                    download
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </a>
                  <Link 
                    to={`/print/tickets/${section}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-ui text-sm hover:border-[hsl(43,74%,52%)] hover:text-[hsl(43,74%,52%)] transition-colors"
                    data-testid="print-section-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </Link>
                </div>
              </div>
              <p className="font-ui text-sm text-white/50">
                {entries.length} documented {entries.length === 1 ? "entry" : "entries"}
              </p>
            </header>
            
            <div className="space-y-8">
              {entries.map((entry) => {
                const statusRollup = getStatusRollup(entry);
                const entryCitations = citations[entry.id] || [];
                
                return (
                  <article 
                    key={entry.id} 
                    className="p-6 md:p-8 bg-[hsl(222,47%,11%,0.5)] border border-white/10"
                    data-testid={`ticket-entry-${entry.id}`}
                  >
                    <header className="flex items-start justify-between gap-4 mb-5">
                      <h2 className="font-display text-xl md:text-2xl text-white">{entry.title}</h2>
                      {statusRollup && <ClaimStatusBadge status={statusRollup} />}
                    </header>
                    
                    <p className="font-editorial text-white/70 leading-relaxed mb-5">{entry.narrative}</p>
                    
                    {entry.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-5">
                        {entry.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary" className="font-ui text-xs bg-white/5 text-white/60 border-white/10">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Evidence Panel */}
                    <div className="border-t border-white/10 pt-5 mt-5">
                      <div className="flex items-center gap-4 flex-wrap">
                        {entry.claim_ids?.length > 0 && (
                          <EvidenceDrawer 
                            claimIds={entry.claim_ids} 
                            title={`Evidence: ${entry.title}`} 
                          />
                        )}
                        
                        {entryCitations.length > 0 && (
                          <span className="font-ui text-sm text-white/40">
                            {entryCitations.length} {entryCitations.length === 1 ? "citation" : "citations"}
                          </span>
                        )}
                      </div>
                      
                      {/* Inline Citations Preview */}
                      {entryCitations.length > 0 && (
                        <div className="mt-5 bg-[hsl(222,47%,8%)] p-5 border-l-2 border-[hsl(43,74%,52%)]">
                          <h4 className="font-ui text-xs uppercase tracking-[0.15em] text-[hsl(43,74%,52%)] mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Primary Sources
                          </h4>
                          <CitationList citations={entryCitations} />
                        </div>
                      )}
                      
                      {/* Primary Documents */}
                      <PrimaryDocuments 
                        entityType="tickets" 
                        entityId={entry.id}
                        title="Attached Documents"
                      />
                    </div>
                  </article>
                );
              })}
              
              {entries.length === 0 && (
                <div className="text-center py-16 text-white/50 font-editorial">
                  <p className="mb-2">No published records are currently available in this category.</p>
                  <p className="text-sm text-white/40">
                    Evidence-backed entries will appear here only after source verification and editorial review.
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default TicketsHubPage;
